import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, FormControlLabel, Skeleton, Switch, Typography } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import { useAuth } from '../../context/EnterpriseAuthContext';
import { loadAutopilotSettings, saveAutopilotSettings } from '../../services/autopilotService';
import { DEFAULT_AUTOPILOT_SETTINGS } from '../../services/autopilotModel';

/** The four switches, in display order. */
export const AUTOPILOT_SWITCHES = Object.freeze([
  {
    key: 'autoSummaries',
    label: 'Automatic summaries',
    help: 'After each recorded meeting, Quill writes the summary, decisions and action items, and spots when a follow-up is needed.',
  },
  {
    key: 'autoFiling',
    label: 'Automatic filing',
    help: 'Quill files each meeting under the customer and project it belongs to. Internal meetings stay unfiled.',
  },
  {
    key: 'autoCreate',
    label: 'Create projects and customers',
    help: 'When no project fits, Quill creates one (and the customer, for a new company). They are labelled, and you can undo them.',
    requires: 'autoFiling',
  },
  {
    key: 'meetingRecaps',
    label: 'Meeting recap emails',
    help: 'An email when a meeting is ready, with the summary, where it was filed and one-click documents. Separate from product emails.',
  },
]);

/**
 * Settings > Autopilot: per-user switches for Meeting Autopilot, all on by default, each saved as
 * soon as it changes.
 * @returns {JSX.Element} Section.
 */
export default function AutopilotSettingsSection() {
  const { state: authState } = useAuth();
  const userId = authState.user?.id || null;
  const [settings, setSettings] = useState({ ...DEFAULT_AUTOPILOT_SETTINGS });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const savingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    loadAutopilotSettings({ userId })
      .then((loaded) => {
        if (!cancelled) setSettings(loaded);
      })
      .catch(() => {
        if (!cancelled) setLoadError("We couldn't load your Autopilot settings.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, reloadKey]);

  /**
   * Save one switch.
   * @param {string} key - Setting key.
   * @param {boolean} value - New value.
   * @returns {Promise<void>}
   */
  const handleToggle = async (key, value) => {
    if (savingRef.current) return;
    savingRef.current = true;
    const previous = settings;
    const next = { ...settings, [key]: value };
    setSettings(next);
    setSaveError('');
    setSavedMessage('');
    try {
      const { settings: saved } = await saveAutopilotSettings({ userId, settings: next });
      setSettings(saved);
      const label = AUTOPILOT_SWITCHES.find((s) => s.key === key)?.label || 'Setting';
      setSavedMessage(`${label} turned ${value ? 'on' : 'off'}.`);
    } catch {
      setSettings(previous);
      setSaveError("We couldn't save that change. Please try again.");
    } finally {
      savingRef.current = false;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesome fontSize="small" />
        Autopilot
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What Quill does on its own after a meeting is recorded. Documents are never created without your click.
      </Typography>

      {loadError ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>Retry</Button>}>
          {loadError}
        </Alert>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 'var(--q-radius-xl)' }}>
          <CardContent sx={{ display: 'grid', gap: 2 }}>
            {AUTOPILOT_SWITCHES.map((item) => {
              const blocked = item.requires && !settings[item.requires];
              const helpId = `settings-autopilot-${item.key}-help`;
              return (
                <Box key={item.key}>
                  {loading ? (
                    <Skeleton variant="rounded" height={32} aria-label={`Loading ${item.label}`} />
                  ) : (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(settings[item.key]) && !blocked}
                          disabled={Boolean(blocked)}
                          onChange={(e) => handleToggle(item.key, e.target.checked)}
                          inputProps={{ 'aria-describedby': helpId }}
                        />
                      }
                      label={item.label}
                    />
                  )}
                  <Typography id={helpId} variant="body2" color="text.secondary" sx={{ ml: { sm: 6 } }}>
                    {blocked ? 'Turn on automatic filing to use this.' : item.help}
                  </Typography>
                </Box>
              );
            })}
            <Box aria-live="polite">
              {saveError && <Alert severity="error">{saveError}</Alert>}
              {!saveError && savedMessage && <Typography variant="body2" color="text.secondary">{savedMessage}</Typography>}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
